/** React imports */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
/** App imports */
import { appContext } from '../../Contexts/AppContext';
import { useGetLog } from '../../Hooks/useQueryLogs';
import { ValidatorProps } from '../../TypeScript/interfaces';
import { LogsApiLogObject } from '@goldspotdiscoveries/atoms';
import { useAppAlert } from '../../Contexts/AppAlertContext';

export const SelectedLogContext = createContext<{
  selectedLog: LogsApiLogObject | null;
  isSelectedLogLoading: boolean;
  setSelectedLogId: (logId?: string) => void;
}>({
  selectedLog: null,
  isSelectedLogLoading: false,
  setSelectedLogId: () => {},
});

/**
 * Validate an optional log id in the path.
 *
 * The authenticated log request is also the access check: the API only returns
 * logs available to the current user for the requested company and project.
 * Company and project access are validated by outer route validators.
 */
const LogIdValidator: React.FC<ValidatorProps> = (
  props: React.PropsWithChildren<ValidatorProps>,
): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logId } = useParams();
  const { activeProject, company } = useContext(appContext);
  const { addAppAlert } = useAppAlert();
  const targetProjectId = activeProject.id || '';
  const {
    data: log,
    error,
    isLoading,
    isError,
  } = useGetLog(company, targetProjectId, logId || '');
  const alertedLogIds = useRef(new Set<string>());

  const setSelectedLogId = useCallback(
    (nextLogId: string = ''): void => {
      const basePath = logId
        ? location.pathname.slice(0, -logId.length - 1)
        : location.pathname;
      const nextPath = nextLogId ? `${basePath}/${nextLogId}` : basePath;

      if (nextPath !== location.pathname) {
        navigate(nextPath, { replace: true });
      }
    },
    [location.pathname, logId, navigate],
  );

  const isSelectedLogLoading: boolean = useMemo(() => {
    if (!logId) return false;
    if (!company || !targetProjectId) return true;
    return isLoading;
  }, [company, isLoading, logId, targetProjectId]);

  const isValidLog = useMemo(() => {
    return (
      !!log &&
      !isError &&
      log.id === logId &&
      log.company === company &&
      log.project === targetProjectId
    );
  }, [company, isError, log, logId, targetProjectId]);

  const selectedLog = useMemo(() => {
    if (!logId || !company || !targetProjectId) return null;
    return isValidLog && log ? log : null;
  }, [company, isValidLog, log, logId, targetProjectId]);

  useEffect(() => {
    if (
      !logId ||
      !company ||
      !targetProjectId ||
      isLoading ||
      isValidLog ||
      alertedLogIds.current.has(logId)
    ) {
      return;
    }

    alertedLogIds.current.add(logId);
    addAppAlert({
      severity: 'error',
      message: t('ALERT_ERROR_GET_LOG'),
      details:
        (error as Error | null)?.message ||
        (error as any)?.error.response?.data?.error ||
        (error as any)?.error?.message ||
        undefined,
    });
    setSelectedLogId('');
  }, [
    addAppAlert,
    error,
    isLoading,
    isValidLog,
    logId,
    setSelectedLogId,
    targetProjectId,
    t,
  ]);
  return (
    <SelectedLogContext.Provider
      value={{
        selectedLog,
        isSelectedLogLoading,
        setSelectedLogId,
      }}
    >
      {props.children}
    </SelectedLogContext.Provider>
  );
};

export default LogIdValidator;
